/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { returnType } from '../ActionSet/ActionSetManager';
import { defaultValue } from './ActionSetSimpleQueryFilter';

const ActionSetSimpleQueryFilterManager: ActionManagerFn<
  'ActionSetSimpleQueryFilter'
> = (platform: Platform): ActionManager<'ActionSetSimpleQueryFilter'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionSetSimpleQueryFilter'),
    defaultValue,
    props: {
      ...baseActionProps,
      attribute: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
      value: {
        label: 'Value',
        type: 'expression',
        required: true,
        refreshCallback: (cb: () => void) => {
          callback = cb;
        },
        returnType: async (
          item: RequiredItem<'ActionSetSimpleQueryFilter'>,
          { appUid, pageId, itemId },
        ) => {
          return returnType({
            appUid,
            pageId,
            itemId,
            attribute: item.props.attribute,
            itemType: item.type,
            platform,
            storeType: 'QUERY_FILTER',
          });
        },
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'attribute') {
        setTimeout(() => {
          callback();
        }, 100);
        return {
          [property]: value,
          value: defaultValue.value,
        };
      }
      return {
        [property]: value,
      };
    },
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return [
        'ActionLoadData',
        'ActionQuery',
        'ActionQueryChart',
        'ActionQuerySelect',
        'TriggerBeforeLoad',
        'TriggerBeforeQuery',
      ].includes(parent.type);
    },
    getDisplayText: (item) => item.props.attribute,
    name: 'Set Simple Query Filter',
    description: `**Overview**<br/>
The Set Simple Query Filter action is a streamlined data constraint component designed to apply a direct equality filter to a data retrieval operation. Unlike more complex filtering actions that require defining operators (like "Greater Than" or "Contains"), this component assumes a strict "Equals" relationship, making it the most efficient way to match specific values.<br/><br/>
This component cannot exist independently; it is strictly an architectural element used within parent query actions (like ActionLoadData, ActionQuery, or ActionQueryChart). It serves as the standard method for passing specific parameters—such as an ID or a status code—to the server-side query to restrict the result set.

**Key Features**<br/>
* **Implicit Equality:** Automatically applies a "Where [Attribute] = [Value]" logic, removing the need for configuration of comparison operators.
* **Type Safety:** Implements dynamic validation (returnType) to ensure that the data type of the provided value matches the selected attribute. If a user selects a "Date" field, the expression editor enforces a Date return type.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions within valid data fetching contexts (Query, LoadData, QueryChart, QuerySelect).
* **Dynamic Values:** The value property supports expressions, allowing the filter criteria to be driven by variables, other form inputs, or calculations at runtime.

**Properties**<br/>
* **Attribute (Required):** A ViewAttributeChooser property.
  * *Function:* Selects the specific field (column) in the database to filter against.
* **Value (Required):** An expression property.
  * *Function:* The value that the attribute must match.
  * *Validation:* Must evaluate to the same data type as the selected Attribute.

**Use Cases**<br/>
* **ID Lookup:** Inside an ActionLoadData, adding a simple filter for OrderID = #{pageParams.id} to fetch a specific record.
* **Status Filtering:** Restricting a "Task List" query to only return items where Status = 'Pending'.
* **Category Selection:** Updating a chart's query to show data only for Category = 'Electronics' based on a user's dropdown selection.`,
    icon: { iconName: 'filter' },
  };
};

export default ActionSetSimpleQueryFilterManager;
